/**
 * ProductSection Component - Refactored
 * Product section untuk checkout-new page
 * Menampilkan produk grouped by division dengan promo display seperti cart
 */

import { Box, Flex, Stack, Tag } from '@chakra-ui/react';
import { Text } from '@diamond/shared/ui';
import { statusFormatter } from '@diamond/shared/utils';
import type {
  OrderDetailData,
  OrderDetailDataWithDivider,
} from '@diamond/sol-buyer/utils';
import {
  bindPromoData,
  buildDivisionPromos,
  readRequiresWeightAdjustment,
  shouldShowEstimatedWeightUi,
} from '@diamond/sol-buyer/utils';
import { memo, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { ProductItem } from './ProductItem';
import { PromoRow } from './PromoRow';
import type { AppliedVoucherInfo, ProductSectionProps } from './types';
import { VoucherFreeGoodsRow } from './VoucherFreeGoodsRow';

function getCancelledReason(reason: string | null | undefined) {
  if (!reason) return null;
  const trimmed = reason.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getDivisionStatusBadgeLabel(division: {
  status?: string;
  reason?: string | null;
  sap_note?: string | null;
}) {
  if (!division.status || division.status === 'draft') return null;

  if (division.status === 'cancelled') {
    return 'Dibatalkan';
  }

  return statusFormatter(division.status, division.reason, division.sap_note);
}

/**
 * Get free goods for a specific division from applied voucher.
 * Only one voucher can be applied at a time, so we use the first voucher.
 */
function getFreeGoodsForDivision(
  appliedVoucher: AppliedVoucherInfo | undefined,
  divisionName: string
) {
  if (!appliedVoucher) {
    return { freeGoods: [], voucherInfo: undefined };
  }

  const freeGoods = appliedVoucher.free_goods || [];
  const matchingFreeGoods = freeGoods.filter(
    (fg) => fg.division?.toLowerCase() === divisionName.toLowerCase()
  );

  return { freeGoods: matchingFreeGoods, voucherInfo: appliedVoucher };
}

/**
 * ProductSection Component
 * Menampilkan semua produk yang di-group by division dengan promo
 *
 * Performance optimizations:
 * - useWatch instead of watch() for isolated field subscriptions
 * - useMemo prevents expensive re-renders for large product lists
 * - React.memo prevents re-renders from parent when props unchanged
 */
function ProductSectionInner({
  embedded = false,
  vouchersData,
  isEstimatedWeightProductEnabled = false,
}: ProductSectionProps) {
  const appliedVouchers = vouchersData?.applied_vouchers || [];
  const rewardByItemCode = vouchersData?.reward_by_item_code;
  const firstAppliedVoucher = appliedVouchers[0];

  // useWatch isolates subscription to specific fields only
  // Unlike watch(), this won't re-render on unrelated form changes
  const itemsRaw = useWatch({ name: 'items' });
  const isSubmitted = useWatch({ name: 'is_submitted' }) || false;

  /**
   * Memoized product grouping and promo processing
   * Prevents expensive operations on every render (flatten, reduce, map, bindPromoData)
   * Only recalculates when items change
   */
  const dataWithPromoAndStatus = useMemo(() => {
    // Defensive check: ensure items is a valid array
    if (!itemsRaw || !Array.isArray(itemsRaw) || itemsRaw.length === 0) {
      return [];
    }

    const items = itemsRaw as OrderDetailData;

    // Step 1: Flatten all products with their parent item metadata
    const allProductsWithMeta = items.flatMap((item) => {
      // Defensive check: ensure item and item.products are valid
      if (!item || !Array.isArray(item.products)) {
        return [];
      }
      return item.products.map((product) => ({
        ...product,
        _parentItemId: item.id,
        _parentPromos: item.promos,
        _parentStatus: item.status,
        _parentSubPoNumber: item.sub_po_number,
        _parentReason: item.reason ?? item.cancel_reason ?? null,
        _parentSapNote: item.sap_note,
      }));
    });

    // Step 2: Group products by product.division
    // Early return if no products after flattening
    if (allProductsWithMeta.length === 0) {
      return [];
    }

    const groupedByProductDivision = allProductsWithMeta.reduce(
      (acc, product) => {
        // Defensive check: skip invalid products
        if (!product || typeof product !== 'object') {
          return acc;
        }
        const division = product.division || 'other';
        if (!acc[division]) {
          acc[division] = [];
        }
        acc[division].push(product);
        return acc;
      },
      {} as Record<string, typeof allProductsWithMeta>
    );

    // Step 3: Create division groups with merged promos
    // Build a map of product ID to division for promo filtering
    const productIdToDivision = new Map<string, string>();
    allProductsWithMeta.forEach((product) => {
      productIdToDivision.set(product.id, product.division || 'other');
    });

    const dataWithDivider = Object.entries(groupedByProductDivision).map(
      ([division, products]) => {
        // Get product IDs in this division for filtering promos
        const productIdsInDivision = new Set(products.map((p) => p.id));

        // Merge promos from all parent items, filtering by product association
        type PromoType = OrderDetailData[number]['promos'];
        const mergedPromos: PromoType = {
          single_free_good_promos: [],
          single_discount_promos: [],
          bundled_free_good_promos: [],
          bundled_discount_promos: [],
          compiled_product_discounts: [],
        };

        // Collect unique parent item IDs to avoid duplicate promo sources
        const seenParentIds = new Set<string>();
        products.forEach((product) => {
          const parentId = product._parentItemId;

          if (!seenParentIds.has(parentId) && product._parentPromos) {
            seenParentIds.add(parentId);
            const promos = product._parentPromos;

            // Filter single_free_good_promos by ref_product_id
            (promos.single_free_good_promos || []).forEach((promo) => {
              const promoProductId = promo.ref_product_id || promo.product_id;
              if (productIdsInDivision.has(promoProductId)) {
                mergedPromos.single_free_good_promos.push(promo);
              }
            });

            // Filter single_discount_promos by product_id
            (promos.single_discount_promos || []).forEach((promo) => {
              if (productIdsInDivision.has(promo.product_id)) {
                mergedPromos.single_discount_promos.push(promo);
              }
            });

            // Filter bundled_free_good_promos by ref_product_id
            (promos.bundled_free_good_promos || []).forEach((promo) => {
              const promoProductId = promo.ref_product_id || promo.product_id;
              if (productIdsInDivision.has(promoProductId)) {
                mergedPromos.bundled_free_good_promos.push(promo);
              }
            });

            // Filter bundled_discount_promos - include all for now as they may apply to multiple products
            // Bundled promos typically apply across products, so we include them in all divisions
            // that have at least one product from the parent item
            (promos.bundled_discount_promos || []).forEach((promo) => {
              mergedPromos.bundled_discount_promos.push(promo);
            });

            // Filter compiled_product_discounts by product_id
            (promos.compiled_product_discounts || []).forEach((promo) => {
              if (productIdsInDivision.has(promo.product_id)) {
                mergedPromos.compiled_product_discounts.push(promo);
              }
            });
          }
        });

        // Remove metadata from products
        const cleanProducts = products.map((p) => {
          const {
            _parentItemId,
            _parentPromos,
            _parentStatus,
            _parentSubPoNumber,
            _parentReason,
            _parentSapNote,
            ...product
          } = p;
          return product;
        });

        const firstReason =
          products
            .map((p) => getCancelledReason(p._parentReason))
            .find((reason) => reason !== null) ?? null;

        return {
          divider: division === 'fnb' ? 'FINE FOOD' : division.toUpperCase(),
          division,
          products: cleanProducts,
          promos: mergedPromos,
          // Use first product's parent metadata for status display
          status: products[0]._parentStatus,
          sub_po_number: products[0]._parentSubPoNumber,
          reason: firstReason,
          sap_note: products[0]._parentSapNote,
        };
      }
    );

    // Bind promo data to products (adds free_good_data, hasDiscount, hasCashback to each product)
    // Transform data to match OrderDetailDataWithDivider type by casting
    const dataWithPromo = bindPromoData(
      dataWithDivider as unknown as OrderDetailDataWithDivider
    );

    // Add back the status information that we need for display
    return dataWithPromo.map((item, index) => ({
      ...item,
      status: dataWithDivider[index].status,
      sub_po_number: dataWithDivider[index].sub_po_number,
      reason: dataWithDivider[index].reason,
      sap_note: dataWithDivider[index].sap_note,
      division: dataWithDivider[index].division,
    }));
  }, [itemsRaw]); // Only recalculate when items change

  // Empty state
  if (dataWithPromoAndStatus.length === 0) {
    return (
      <Box
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="lg"
        p={{ base: 6, md: 8 }}
        textAlign="center"
      >
        <Text fontSize="md" color="gray.600">
          Tidak ada produk
        </Text>
      </Box>
    );
  }

  // Render division groups
  return (
    <Stack spacing={6}>
      {/* Title */}
      <Text
        fontSize={{ base: 'lg', md: 'xl' }}
        fontWeight="bold"
        color="gray.900"
      >
        Rincian Produk
      </Text>

      {/* Division Groups - Each division in separate box */}
      {dataWithPromoAndStatus.map((division, divIdx) => {
        const { promos } = division;

        const divisionPromos = buildDivisionPromos(promos);

        return (
          <Box
            key={`division-${divIdx}`}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
            bg="white"
          >
            {/* Division Header with Blue Background */}
            <Flex
              bg="#0941A8"
              color="white"
              px={{ base: 3, md: 4 }}
              py={{ base: 2, md: 3 }}
              fontWeight="bold"
              textTransform="uppercase"
              fontSize={{ base: 'sm', md: '12px' }}
              justify="space-between"
              align="center"
            >
              <Text color="white">
                {division.divider}
                {isSubmitted && division.sub_po_number
                  ? ` - ${division.sub_po_number}`
                  : ''}
              </Text>
              {division.status && division.status !== 'draft' && (
                <Tag
                  size={{ base: 'sm', md: 'md' }}
                  borderRadius="full"
                  colorScheme="blue"
                  variant="solid"
                  textTransform="capitalize"
                >
                  {getDivisionStatusBadgeLabel(division)}
                </Tag>
              )}
            </Flex>

            {/* Division Content */}
            <Stack spacing={4} p={{ base: 3, md: 4 }}>
              {/* Products in this division */}
              {division.products.map((product, prodIdx) => {
                // Lookup voucher discount for this product by item_code
                const itemCode = product.item_code?.toString() || '';
                const voucherRewards = rewardByItemCode?.[itemCode];
                const voucherDiscounts = voucherRewards?.discounts || [];

                return (
                  <ProductItem
                    key={`product-${divIdx}-${prodIdx}`}
                    product={product}
                    promos={promos}
                    voucherDiscount={voucherDiscounts[0]}
                    voucherInfo={firstAppliedVoucher}
                    showEstimatedWeightUi={shouldShowEstimatedWeightUi(
                      isEstimatedWeightProductEnabled,
                      readRequiresWeightAdjustment(product)
                    )}
                  />
                );
              })}

              {/* Banners Stack - mirroring CartList pattern */}
              <Stack spacing={4} mt={2}>
                {/* Division-level Promo Rows */}
                {divisionPromos.map((promo) => {
                  if (promo.type === 'free-item' && promo.item) {
                    return (
                      <PromoRow
                        key={promo.promoId}
                        type="free-item"
                        duration={promo.duration}
                        text={promo.text}
                        promoId={promo.promoId}
                        item={promo.item}
                        images={promo.images}
                      />
                    );
                  }
                  if (promo.type === 'discount') {
                    return (
                      <PromoRow
                        key={promo.promoId}
                        type="discount"
                        duration={promo.duration}
                        text={promo.text}
                        promoId={promo.promoId}
                        discountDisplay={promo.discountDisplay}
                      />
                    );
                  }
                  if (promo.type === 'cashback') {
                    return (
                      <PromoRow
                        key={promo.promoId}
                        type="cashback"
                        duration={promo.duration}
                        text={promo.text}
                        promoId={promo.promoId}
                      />
                    );
                  }
                  return null;
                })}

                {/* Voucher Free Goods */}
                {getFreeGoodsForDivision(
                  firstAppliedVoucher,
                  division.division || ''
                ).freeGoods.map((freeGood, fgIdx) => (
                  <VoucherFreeGoodsRow
                    key={`voucher-fg-${divIdx}-${fgIdx}`}
                    freeGood={freeGood}
                    voucherInfo={firstAppliedVoucher}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

// Memoize to prevent unnecessary re-renders from parent
export const ProductSection = memo(ProductSectionInner);

// Re-export components for external usage if needed
export { ProductItem } from './ProductItem';
export type { AppliedVoucherInfo } from './types';
export { VoucherFreeGoodsRow } from './VoucherFreeGoodsRow';
